import {changeIssuesTabCounter} from '@github-ui/issues-tab-counter'
import {
  ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE,
  ISSUES_BULK_UPDATE_TIMEOUT_MS,
} from '@github-ui/issues-bulk-actions/job-status'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import type {LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import type {IssueListItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export type RepoIssuesBulkOptimisticUpdate = Readonly<{
  addedLabels?: ReadonlyArray<
    Pick<LabelPickerLabel, 'id' | 'name' | 'nameHTML' | 'color'> & Partial<Pick<LabelPickerLabel, 'description'>>
  >
  addedAssignees?: ReadonlyArray<
    Pick<AssigneePickerAssignee, 'id' | 'login' | 'avatarUrl'> &
      Partial<Pick<AssigneePickerAssignee, 'name' | 'profileResourcePath' | 'isCopilot' | 'isAgent'>>
  >
  milestone?: Pick<MilestonePickerMilestone, 'id' | 'title' | 'url'> | null
  issueType?: Pick<IssueTypePickerIssueType, 'id' | 'name' | 'color'> | null
}>

export type RepoIssuesBulkUpdate =
  | Readonly<{state: 'OPEN'; stateReason?: never}>
  | Readonly<{state: 'CLOSED'; stateReason: 'COMPLETED' | 'NOT_PLANNED'}>
  | Readonly<{addedLabelIds: readonly string[]; removedLabelIds: readonly string[]}>
  | Readonly<{applyAssigneeIds: readonly string[]; removeAssigneeIds: readonly string[]}>
  | Readonly<{addToProjectIds: readonly string[]; removeFromProjectIds: readonly string[]}>
  | Readonly<{milestoneId: string}>
  | Readonly<{clearMilestone: true}>
  | Readonly<{issueTypeId: string}>
  | Readonly<{clearIssueType: true}>

export type RepoIssuesBulkTarget = Readonly<{ids: readonly string[]}> | Readonly<{query: string; repositoryId: string}>

export type RepoIssuesNotificationState = 'READ' | 'UNREAD'

// Matches Platform::Mutations::Shared::IssuesNotificationState::LIMIT.
export const REPO_ISSUES_MARK_NOTIFICATION_STATE_LIMIT = 50

type IssueForTabCounter = Pick<IssueListItem, 'globalId' | 'id' | 'itemType' | 'state'>

export function applyRepoIssuesBulkOptimisticUpdate(
  issue: IssueListItem,
  update: RepoIssuesBulkUpdate,
  optimisticUpdate?: RepoIssuesBulkOptimisticUpdate,
): IssueListItem {
  if ('state' in update) {
    return {
      ...issue,
      state: update.state,
      stateReason: update.state === 'OPEN' ? 'REOPENED' : update.stateReason,
      closed: update.state === 'CLOSED',
      closedAt: update.state === 'OPEN' ? null : issue.closed ? issue.closedAt : new Date().toISOString(),
    }
  }
  if ('addedLabelIds' in update) {
    const removedIds = new Set(update.removedLabelIds)
    const retainedLabels = issue.labels.filter(label => !removedIds.has(String(label.id)))
    const retainedIds = new Set(retainedLabels.map(label => String(label.id)))
    const addedLabels = (optimisticUpdate?.addedLabels ?? [])
      .filter(label => !retainedIds.has(label.id))
      .map(label => ({
        id: label.id,
        name: label.name,
        nameHtml: label.nameHTML,
        color: label.color,
        description: label.description ?? undefined,
      }))
    return {...issue, labels: [...retainedLabels, ...addedLabels]}
  }
  if ('applyAssigneeIds' in update) {
    const removedIds = new Set(update.removeAssigneeIds)
    const retainedAssignees = issue.assignees.filter(assignee => !removedIds.has(String(assignee.id)))
    const retainedIds = new Set(retainedAssignees.map(assignee => String(assignee.id)))
    const addedAssignees = (optimisticUpdate?.addedAssignees ?? [])
      .filter(assignee => !retainedIds.has(assignee.id))
      .map(assignee => ({
        id: assignee.id,
        login: assignee.login,
        name: assignee.name ?? undefined,
        avatarUrl: assignee.avatarUrl,
        profileResourcePath: assignee.profileResourcePath ?? '',
        isCopilot: assignee.isCopilot ?? false,
        isAgent: assignee.isAgent ?? false,
      }))
    return {...issue, assignees: [...retainedAssignees, ...addedAssignees]}
  }
  if ('milestoneId' in update || 'clearMilestone' in update) {
    return {...issue, milestone: optimisticUpdate?.milestone ?? null}
  }
  if ('issueTypeId' in update || 'clearIssueType' in update) {
    return {...issue, issueType: optimisticUpdate?.issueType ?? null}
  }
  return issue
}

export function updateIssuesTabCounter({
  issues,
  update,
  failedIssueIds,
  allMatching,
  direction = 1,
}: {
  issues: IssueForTabCounter[]
  update: RepoIssuesBulkUpdate
  failedIssueIds: ReadonlySet<string>
  allMatching: boolean
  direction?: 1 | -1
}): void {
  let delta = 0
  if (!allMatching && 'state' in update) {
    for (const issue of issues) {
      if (failedIssueIds.has(getListItemIdentity(issue))) continue
      if (issue.state === 'OPEN' && update.state === 'CLOSED') delta -= 1
      else if (issue.state === 'CLOSED' && update.state === 'OPEN') delta += 1
    }
  }
  if (delta !== 0) changeIssuesTabCounter(delta * direction)
}

function mutationJobId(payload: unknown): string {
  if (!payload || typeof payload !== 'object') throw new Error('Bulk update returned an invalid response')
  if ('errors' in payload && Array.isArray(payload.errors) && payload.errors.length > 0) {
    const firstError = payload.errors[0]
    throw new Error(
      firstError && typeof firstError === 'object' && 'message' in firstError && typeof firstError.message === 'string'
        ? firstError.message
        : 'Bulk update failed',
    )
  }

  if (!('data' in payload) || !payload.data || typeof payload.data !== 'object') {
    throw new Error('Bulk update returned an invalid response')
  }
  const data = payload.data as Record<string, unknown>
  const mutation = data.updateIssuesBulk ?? data.updateIssuesBulkByQuery
  if (!mutation || typeof mutation !== 'object' || !('jobId' in mutation) || typeof mutation.jobId !== 'string') {
    throw new Error('Bulk update did not return a job ID')
  }
  return mutation.jobId
}

export async function startIssuesBulkUpdate({
  target,
  update,
}: {
  target: RepoIssuesBulkTarget
  update: RepoIssuesBulkUpdate
}): Promise<string> {
  if ('ids' in target && (target.ids.length < 1 || target.ids.length > 1000)) {
    throw new Error('Select between 1 and 1,000 issues')
  }

  const abortController = new AbortController()
  const timeout = setTimeout(() => abortController.abort(), ISSUES_BULK_UPDATE_TIMEOUT_MS)

  try {
    const response = await reactFetchJSON('/_ghui/item-pickers/bulk_actions', {
      method: 'POST',
      body: {...target, ...update},
      signal: abortController.signal,
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    return mutationJobId(await response.json())
  } catch (error) {
    if (abortController.signal.aborted) throw new Error(ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE)
    throw error
  } finally {
    clearTimeout(timeout)
  }
}

// Read state is per viewer and resolves to a single UPDATE, so unlike
// `startIssuesBulkUpdate` this answers inline and has no job to poll.
export async function markIssuesNotificationState({
  ids,
  state,
}: {
  ids: readonly string[]
  state: RepoIssuesNotificationState
}): Promise<void> {
  if (ids.length < 1 || ids.length > REPO_ISSUES_MARK_NOTIFICATION_STATE_LIMIT) {
    throw new Error(`Select between 1 and ${REPO_ISSUES_MARK_NOTIFICATION_STATE_LIMIT} issues`)
  }

  const response = await reactFetchJSON('/_ghui/item-pickers/notification_states', {
    method: 'POST',
    body: {ids, state},
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
}
